import { z } from 'zod';

import { staticFeatureFlags } from '@/config/features';
import { ControlType } from '@/constants/institution';
import { institutionIndividualSchema } from '@/features/institution/schemas/individual';
import i18n from '@/lib/i18n';
import { type InstitutionDetailsExecution as InstitutionDetailsExecutionApi, institutionDetailsAlgorithmSchema } from '@/services/api/profile';
import { toDatePartsFromString, toDateStringFromParts } from '@/utils/date';

const getIndividualsError = () =>
  staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED
    ? i18n.t('institutionDetails.execution.errors.individualsExactlyOne')
    : i18n.t('institutionDetails.execution.errors.individuals');

export const institutionFuturesExecutionSchema = z.discriminatedUnion(
  'execution',
  [
    z.object({
      execution: z.literal(ControlType.INDIVIDUAL),
      individuals: z
        .array(institutionIndividualSchema, { error: getIndividualsError })
        .min(1, { error: getIndividualsError })
        .max(staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED ? 1 : Number.POSITIVE_INFINITY, { error: getIndividualsError }),
    }),
    z.object({
      execution: z.literal(ControlType.ALGORITHM),
      algorithm: institutionDetailsAlgorithmSchema,
    }),
  ],
  {
    error: i18n.t('institutionDetails.execution.errors.execution'),
  },
);

export type InstitutionFuturesExecution = z.infer<typeof institutionFuturesExecutionSchema>;

export type InstitutionFuturesExecutionOrDefaults = InstitutionFuturesExecution | Partial<InstitutionFuturesExecution>;

export const institutionFuturesExecutionToInstitutionDetailsExecutionApi = (execution: InstitutionFuturesExecution): InstitutionDetailsExecutionApi => {
  if (execution.execution === ControlType.INDIVIDUAL) {
    return {
      ...execution,
      individuals: execution.individuals.map((individual) => ({
        ...individual,
        dateOfBirth: toDateStringFromParts(individual.dateOfBirth),
      })),
    };
  }

  return execution;
};

export const institutionDetailsExecutionApiToInstitutionFuturesExecution = (execution: InstitutionDetailsExecutionApi): InstitutionFuturesExecution => {
  if (execution.execution === ControlType.INDIVIDUAL) {
    return {
      ...execution,
      individuals: execution.individuals.map((individual) => ({
        ...individual,
        dateOfBirth: toDatePartsFromString(individual.dateOfBirth),
      })),
    };
  }

  return execution;
};
